import { useEffect, useRef, useState } from "react";
import { ApiError, demoApi, entriesStreamUrl } from "@/api/client";
import type { Entry } from "@/api/types";
import { describeReadFailure } from "@/lib/apiFailure";

const POLL_INTERVAL_MS = 3000;

/** Stream-reopen backoff. Same rhythm as `offline/drainTriggers.ts`, deliberately. */
const FIRST_REOPEN_MS = 2_000;
const MAX_REOPEN_MS = 30_000;
/** How long a freshly-arrived row stays visually flagged as "new". */
export const HIGHLIGHT_DURATION_MS = 2500;

export type EntriesStatus = "loading" | "ready" | "error";

/**
 * Which of the three tiers below is actually carrying the feed right now.
 *
 * EXISTS BECAUSE THE DASHBOARD CLAIMED "Live" UNCONDITIONALLY. The masthead pill was
 * static JSX with a CSS pulse on the dot — it said Live whether the stream was open,
 * dropped, or had never connected at all, which makes it a status indicator that cannot
 * report bad status. The hook has always known the difference (`onopen` stands the
 * fallback down, `onerror` arms it); it simply never told anyone.
 *
 *   "connecting" — no successful stream open yet, and no failure either.
 *   "live"       — SSE is open AND a poll has reconciled the gap it may have left.
 *   "degraded"   — the polling fallback is armed: the stream dropped, never opened, or
 *                  EventSource does not exist here. The feed still works, 3s behind.
 */
/**
 * `signed-out` is not a transport tier like the other three, and it is here because the
 * badge that renders them was the only thing on screen claiming the wall was alive.
 *
 * MEASURED 2026-09-07. A session was invalidated server-side — a 12-hour absolute expiry
 * reached, or an owner switching that login off in Settings, which the screen says "ends
 * whatever session that person is already holding". The wall was left alone, as a wall is.
 * Fifty seconds later it still showed the full dashboard, the last figures, and a **Live**
 * badge; a visitor's entry posted in that state **never arrived**. Nothing said anything was
 * wrong, because every request that could have said so was being suppressed as a transient.
 *
 * That is exactly `signals-that-cannot-go-red`: the one failure the badge exists to report
 * was the one it could not. A dead session is not an outage to ride out — no retry fixes it,
 * so it must be named.
 */
export type FeedTransport = "connecting" | "live" | "degraded" | "signed-out";

export interface UseEntriesResult {
  entries: Entry[];
  status: EntriesStatus;
  error: string | null;
  /** IDs that arrived on the most recent poll and are still within their highlight window. */
  newEntryIds: ReadonlySet<string>;
  /**
   * Every id that has arrived (poll or stream) since THIS hook instance mounted —
   * i.e. since "now", the same cursor the SSE stream itself starts from (see the
   * class doc below). Unlike `newEntryIds` this never shrinks; a consumer that
   * needs "what has happened since the dashboard opened" (Dashboard.tsx's live
   * KPI figures) reads this, not the transient highlight set. Resets to empty on
   * remount, same as everything else here — this hook has no concept of a day,
   * only of the session watching it.
   */
  sessionArrivalIds: ReadonlySet<string>;
  /**
   * The same arrivals, CARRYING THEIR OWN DATA rather than only their ids.
   *
   * MEASURED ON A RUNNING WALL, 2026-09-08. Dashboard.tsx derived its live KPI delta as
   * `entries.filter(e => sessionArrivalIds.has(e.id))` — the ids never shrink, but
   * `entries` is a WINDOW. Thirty visitor entries of $10 took Cash on hand to −$300; a
   * single API blip dropped the feed back to the server's twenty-five most recent, and the
   * card read **−$250**. Fifty dollars of visitors' money reappeared on the screen the
   * audience is watching, with the pill still reading "Live" and nothing to say it had
   * happened.
   *
   * The set was never wrong — the derivation was, because it could only see arrivals the
   * feed still happened to be holding. This array is what "everything since the dashboard
   * opened" actually means, and it is what money must be summed from. Cleared with the id
   * set on a wipe, for the same reason and in the same place.
   */
  sessionArrivals: readonly Entry[];
  /** Which tier is carrying the feed — see FeedTransport. The Dashboard's "Live" pill
   *  renders this rather than asserting it. */
  feedTransport: FeedTransport;
  /**
   * Increments each time the server reports the ledger was wiped underneath this feed
   * (`event: reset` — see the listener below). Zero until it happens.
   *
   * A consumer that derives figures from anything OTHER than `entries` must re-read on a
   * change, because this hook can only clear its own state. Dashboard.tsx passes it to
   * useBalanceSheet and useProfitAndLoss for exactly that reason.
   */
  ledgerGeneration: number;
}

/**
 * Live-updating entries feed for the laptop dashboard.
 *
 * ============================================================================
 * THREE TIERS, DEGRADING IN ORDER. This matters more than it looks: the demo
 * spec calls instant arrival on the laptop the one non-negotiable feature, and
 * the booth runs on a hotspot in a hall full of interference.
 *
 *   1. PowerSync      — not wired yet. Blocked on Docker + DC access (Q3).
 *   2. SSE  ← ACTIVE  — GET /api/demo/entries/stream. Real server push, and it
 *                       needs none of PowerSync's infrastructure, which is why
 *                       it exists: the critical path stops depending on an
 *                       unanswered question.
 *   3. Polling        — the safety net below. Only runs if SSE never connects
 *                       or drops, so a proxy that mangles text/event-stream
 *                       degrades the demo instead of killing it.
 *
 * `UseEntriesResult` stays identical across all three, so Dashboard.tsx never
 * changes — only this hook's internals.
 *
 * NOTE: EventSource cannot send custom headers, so the tenant goes on the query
 * string here. The server accepts both and prefers the header. That is a real
 * browser limitation, not a shortcut.
 * ============================================================================
 */
export function useEntries(): UseEntriesResult {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [status, setStatus] = useState<EntriesStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [newEntryIds, setNewEntryIds] = useState<ReadonlySet<string>>(new Set());
  const [sessionArrivalIds, setSessionArrivalIds] = useState<ReadonlySet<string>>(new Set());
  const [sessionArrivals, setSessionArrivals] = useState<readonly Entry[]>([]);
  const [feedTransport, setFeedTransport] = useState<FeedTransport>("connecting");
  /**
   * Bumped every time the server says the ledger was wiped underneath us.
   *
   * Dashboard.tsx passes it to useBalanceSheet/useProfitAndLoss so the KPI cards
   * re-read too — this hook only owns the feed, and a cleared feed sitting under a
   * Cash-on-hand card still showing the deleted entry's money is the same lie in a
   * different box.
   */
  const [ledgerGeneration, setLedgerGeneration] = useState(0);

  const knownIdsRef = useRef<Set<string>>(new Set());
  const inFlightRef = useRef(false);
  /** Guards the opening render so seeded history does not animate in as "new". */
  const firstLoadDoneRef = useRef(false);
  const highlightTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    /**
     * Mark ids as freshly arrived and schedule them to stop glowing.
     * Shared by both the stream and the fallback poll so a row looks identical
     * however it reached the screen.
     */
    function flagAsNew(arrived: Entry[]): void {
      if (arrived.length === 0) return;
      const ids = arrived.map((entry) => entry.id);
      setNewEntryIds((prev) => new Set([...prev, ...ids]));
      // Permanent record, never cleared by the highlight timer below — see
      // `sessionArrivalIds` on UseEntriesResult for why this needs its own set
      // rather than reusing the transient one.
      setSessionArrivalIds((prev) => new Set([...prev, ...ids]));
      // And the arrivals themselves, because `entries` is a window and money must not be
      // summed from a window — see `sessionArrivals` on UseEntriesResult for the measurement.
      // Deduped: the poll and the stream can both report the same arrival.
      setSessionArrivals((prev) => {
        const seen = new Set(prev.map((entry) => entry.id));
        const fresh = arrived.filter((entry) => !seen.has(entry.id));
        return fresh.length === 0 ? prev : [...prev, ...fresh];
      });
      for (const id of ids) {
        const existing = highlightTimersRef.current.get(id);
        if (existing) clearTimeout(existing);
        const timer = setTimeout(() => {
          setNewEntryIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          highlightTimersRef.current.delete(id);
        }, HIGHLIGHT_DURATION_MS);
        highlightTimersRef.current.set(id, timer);
      }
    }

    /** @returns true only if the feed was successfully reconciled against the server. */
    async function poll(): Promise<boolean> {
      if (inFlightRef.current) return false; // defensive: don't stack requests on a slow network
      inFlightRef.current = true;
      try {
        const latest = await demoApi.getEntries(controller.signal);
        // Torn down mid-request: the data is fine but this hook is gone, so nothing
        // was reconciled into a live view and the caller must not treat it as such.
        if (cancelled) return false;

        const arrivedIds = latest
          .filter((e) => !knownIdsRef.current.has(e.id))
          .map((e) => e.id);

        knownIdsRef.current = new Set(latest.map((e) => e.id));
        setEntries(latest);
        setStatus("ready");
        setError(null);

        /*
          A SUCCESSFUL POLL WHILE THE STREAM IS OPEN MEANS THE STREAM IS CARRYING US, and
          this is where that gets noticed — because `onopen` alone could miss it forever.

          `onopen` calls `poll()` and only calls stopPollingFallback() when it returns true.
          The first line of `poll()` bails with FALSE when another poll is already in
          flight — a sound guard against stacking requests, and once the 3-second fallback
          is running an overlap is likely rather than rare. Nothing retried, so one missed
          coincidence left the pill reading "Reconnecting" for the life of the page.

          MEASURED 2026-09-07 with a real `docker restart` of the API: the stream reconnected
          (HTTP 200), entries kept arriving, and the badge still read "Reconnecting" 175
          seconds later. Verified as PRE-EXISTING by disabling the session re-check added the
          same day and reproducing it unchanged.

          It is the mirror of the defect fixed hours earlier — a badge that said "Live" over a
          dead session. This one cannot go GREEN again, and it is on screen behind an operator
          all day telling a prospect something is wrong when nothing is.

          Self-healing on purpose: every later poll gets the same chance, so no single missed
          moment is permanent.
        */
        if (source?.readyState === EventSource.OPEN) stopPollingFallback();

        // Suppress the highlight on the very first load: on a laptop opened at
        // 8am every seeded row would otherwise flash in as if it had just
        // arrived, which is exactly the signal we want reserved for a real
        // visitor's entry landing.
        if (!firstLoadDoneRef.current) {
          firstLoadDoneRef.current = true;
        } else {
          flagAsNew(latest.filter((entry) => arrivedIds.includes(entry.id)));
        }
        return true;
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return false;

        // A FAILED RECONCILE MUST NOT BLANK A FEED THE STREAM IS STILL CARRYING.
        //
        // Dashboard.tsx hides the whole activity list behind `hasError`, so one rejected
        // GET /entries replaced a working live wall with "Can't reach EasyFarm right now."
        // — while the SSE stream was open and pushing rows into a list nobody could see.
        // The wall said it could not reach EasyFarm at the moment it was reaching EasyFarm,
        // which is the failure this hook's three tiers exist to prevent.
        //
        // Caught 2026-09-06 by "the wall recovers when the stream drops mid-demo", which
        // blocks REST on purpose and then requires the row to arrive down the reconnected
        // stream. It went red without any change to the code it covers: the reconciling
        // poll that runs on `onopen` is exactly the blocked request, and whether its
        // rejection rendered before the streamed row did was a race the suite happened to
        // win before and lose after. A test that flips on timing was reporting a real
        // defect the whole time.
        //
        // THE TEST IS readyState, NOT feedTransport, and the difference is the whole fix.
        // `feedTransport` only becomes "live" after a poll SUCCEEDS (see stopPollingFallback's
        // remark) — so in exactly the situation this guard is for, where polls are failing, it
        // is never "live" and the guard would never fire. An OPEN EventSource is the thing
        // that is actually true: the browser has a working connection to this server.
        //
        // Narrow on purpose. It suppresses the error only while the stream is OPEN and there
        // is already something on screen. An empty wall, or one whose stream is down or still
        // connecting, still says so — nothing here can hide a genuine outage.
        // A 401 IS NOT A TRANSIENT, and it must be tested BEFORE the guard below. That guard
        // exists to stop a failed reconcile blanking a wall the stream is still carrying —
        // correct for an outage, and exactly wrong here: it suppressed the only signal that
        // the session had ended, so the badge stayed "Live" over a feed that could never
        // update again. Retrying cannot fix a session that no longer exists.
        if (err instanceof ApiError && err.status === 401) {
          setFeedTransport("signed-out");
          return false;
        }

        if (source?.readyState === EventSource.OPEN && knownIdsRef.current.size > 0) return false;

        setStatus("error");
        setError(describeReadFailure(err));
        return false;
      } finally {
        inFlightRef.current = false;
      }
    }

    /** Merge one pushed entry into the feed, newest first, ignoring duplicates. */
    function ingest(entry: Entry): void {
      if (cancelled || knownIdsRef.current.has(entry.id)) return;
      knownIdsRef.current.add(entry.id);

      setEntries((prev) => [entry, ...prev]);
      setStatus("ready");
      setError(null);
      flagAsNew([entry]);
    }

    // Tier 1: SSE. The initial GET still runs, because the stream deliberately
    // starts from "now" rather than replaying history — otherwise every laptop
    // refresh would re-animate the whole day's entries as if they were new.
    void poll();

    let intervalId: ReturnType<typeof setInterval> | undefined;
    let source: EventSource | undefined;

    /** Tier 3. Armed only when the stream is not carrying us. */
    function startPollingFallback(): void {
      // Set the transport EVEN IF the interval is already running. onerror fires on
      // every failed reconnect attempt, and an early return before this line would
      // leave the pill reading "live" for the whole of a second outage.
      if (!cancelled) setFeedTransport("degraded");
      if (intervalId !== undefined || cancelled) return;
      intervalId = setInterval(() => void poll(), POLL_INTERVAL_MS);
    }

    function stopPollingFallback(): void {
      // "live" is declared HERE and nowhere else -- at the one point the code already
      // treats as "the stream is carrying us and the gap is reconciled". Setting it in
      // onopen instead would call the feed live while the fallback was still running
      // because the reconciling poll had not returned.
      if (!cancelled) setFeedTransport("live");
      if (intervalId === undefined) return;
      clearInterval(intervalId);
      intervalId = undefined;
    }

    /**
     * REOPENING THE STREAM, because EventSource does not always do it for us.
     *
     * The comment that used to sit on `onerror` read "EventSource reconnects on its own,
     * so this is not fatal". That is true for a dropped connection and FALSE for the
     * error a booth actually hits. MEASURED in the browser, 2026-09-06:
     *
     *     valid stream            -> readyState 1 (OPEN)
     *     non-event-stream reply  -> readyState 2 (CLOSED — never retries)
     *
     * A proxy 502 while the API restarts is a non-event-stream reply, so the browser
     * treats it as fatal, closes the stream for good, and nothing here ever built a new
     * one. Reproduced end to end: stop the API, bring it back, and ninety seconds later a
     * FRESH EventSource to the identical URL opens instantly while the dashboard's own is
     * still gone.
     *
     * The visible result is the Live Wall silently and permanently downgrading to
     * three-second polling for the rest of the day, with the pill reading "Reconnecting"
     * while nothing is reconnecting — a status describing an action the code was not
     * taking. The wall runs unattended on a laptop; one venue wifi hiccup and the
     * "server push, not a refresh timer" differentiator is a refresh timer.
     *
     * Backoff matches `offline/drainTriggers.ts` — 2s, doubling, capped at 30s — because
     * it is the same problem and one retry rhythm in this codebase is enough.
     */
    let reopenTimer: ReturnType<typeof setTimeout> | undefined;
    let reopenDelay = FIRST_REOPEN_MS;

    function scheduleReopen(): void {
      if (cancelled || reopenTimer !== undefined) return;
      source?.close();
      reopenTimer = setTimeout(() => {
        reopenTimer = undefined;
        openStream();
      }, reopenDelay);
      reopenDelay = Math.min(reopenDelay * 2, MAX_REOPEN_MS);
    }

    function openStream(): void {
      if (cancelled) return;
      try {
      source = new EventSource(entriesStreamUrl());

      // THE LEDGER WENT BACKWARDS. Everything else in this hook is additive, because
      // the stream is additive — this is the one frame that says "what you are showing
      // is gone". It carries no payload on purpose: the fix is to re-read the server,
      // not to apply a diff (see the endpoint's own remarks).
      //
      // Measured 2026-09-06, before the server could send this: booth entry created,
      // the wall showed it and Cash on hand read -$123; POST /reset emptied the tenant
      // to zero rows; the wall still showed the deleted row, still read -$123, and the
      // pill still said LIVE. That is the gesture Christy makes between prospects, so
      // the next visitor would have been looking at the last one's receipt.
      source.addEventListener("reset", () => {
        if (cancelled) return;
        knownIdsRef.current = new Set();
        // Re-arm the opening-render guard. Every row the refetch brings back is about to
        // look "new" to this hook, and flashing a whole reseeded ledger in would say a
        // dozen visitors just arrived at once. Nothing arrived; we are re-reading.
        firstLoadDoneRef.current = false;
        setEntries([]);
        // sessionArrivalIds is documented as never shrinking — "what has arrived since the
        // dashboard opened". A wipe is the one event that makes that meaningless: the rows
        // it names are gone, and Dashboard.tsx derives its live KPI delta from them, so
        // leaving it would keep the deleted entry's money on the Cash-on-hand card.
        setSessionArrivalIds(new Set());
        setSessionArrivals([]);
        setLedgerGeneration((n) => n + 1);
        void poll();
      });

      source.addEventListener("entry", (ev) => {
        try {
          ingest(JSON.parse((ev as MessageEvent<string>).data) as Entry);
        } catch {
          // A malformed frame must not tear down a booth display. Skip it; the
          // next event, or the fallback poll, corrects the view.
        }
      });

      // RECONCILE BEFORE STANDING DOWN. The stream seeds its cursor from the newest
      // entry that exists AT CONNECT TIME and sends only what follows — it never
      // replays. So anything written while the connection was down is invisible to the
      // reconnected stream, and standing the fallback down the instant the stream
      // reopens means trusting a tier that cannot see the gap it just left.
      //
      // Gating the stand-down on a SUCCESSFUL poll costs one request per reconnect and
      // removes the assumption. It matters most in the asymmetric case — the stream
      // healthy while GET /entries is not, e.g. a proxy that routes text/event-stream
      // correctly and the JSON endpoint badly — where the feed would otherwise be left
      // with no safety net at all.
      //
      // Honest scope: this is defence, not a fix for an observed failure. In the
      // ordinary case polling (POLL_INTERVAL_MS) reconciles long before `onopen`
      // fires, because the server sends nothing until its first keep-alive. It is
      // cheap and it removes a way to be silently wrong.
      source.onopen = () => {
        // A successful open earns a fresh budget, so a stream that flaps hourly does not
        // creep up to the 30s cap and stay there.
        reopenDelay = FIRST_REOPEN_MS;
        void poll().then((reconciled) => {
          if (reconciled) stopPollingFallback();
        });
      };

      // Polling covers the gap either way — during an outage the laptop must not sit
      // frozen in front of a prospect. What is conditional is the REOPEN: while the
      // browser is still retrying by itself (readyState CONNECTING) building a second
      // stream would just double the traffic. Only CLOSED is the browser saying it has
      // given up, and that is the case nothing used to handle.
      source.onerror = () => {
        startPollingFallback();
        if (source?.readyState === EventSource.CLOSED) scheduleReopen();
      };
      } catch {
        // No EventSource at all (very old browser, or a test environment).
        startPollingFallback();
      }
    }

    openStream();

    return () => {
      cancelled = true;
      controller.abort();
      source?.close();
      if (reopenTimer !== undefined) clearTimeout(reopenTimer);
      stopPollingFallback();

      // Release the in-flight guard on teardown.
      //
      // Without this the dashboard hangs on "Loading the live feed…" forever in
      // development. React StrictMode mounts, tears down, and remounts: mount 1
      // sets the guard and suspends on its await; the cleanup aborts it; mount 2
      // calls poll(), still sees the guard set (mount 1's finally has not run
      // yet), and returns immediately — so the only real initial load is skipped
      // and status never leaves "loading".
      //
      // Safe because the aborted request cannot touch state anyway: its closure
      // has `cancelled = true` and its own AbortController is already aborted.
      inFlightRef.current = false;
      for (const timer of highlightTimersRef.current.values()) clearTimeout(timer);
      highlightTimersRef.current.clear();
    };
  }, []);

  return {
    entries,
    status,
    error,
    newEntryIds,
    sessionArrivalIds,
    sessionArrivals,
    feedTransport,
    ledgerGeneration,
  };
}
